import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import useModalEffect from '../../../hooks/useModalEffect';

const ViewSalesTarget = ({ isOpen, onClose, data }) => {
  useModalEffect(isOpen);
  ;

  // Cleanup effect when component unmounts
  useEffect(() => {
    return () => {
      // Clean up any remaining modal states when component unmounts
      const cleanupModal = () => {
        // Remove modal scroll lock
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.body.classList.remove('modal-backdrop');
      };
      
      cleanupModal();
    };
  }, []);


  // Cleanup effect: reset modal body state when component unmounts.
  useEffect(() => {
    return () => {
      // React owns the backdrop DOM (createPortal); only reset body state here.
      // Remove modal-open class from body
      document.body.classList.remove('modal-open');
    };
  }, []);

  if (!data) return null;

  const formatCurrency = (value) => {
    if (!value) return '0';
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d]/g, '')) : parseFloat(value);
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numValue);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (error) {
      return dateString;
    }
  };

  return (
    <>
      <style>{`
        #modal-view-sales-target .inv-footer {
          border-top: 1px solid #e9ecef !important;
          background: #fff;
          padding: 1rem 1.5rem;
          display: flex; align-items: center; justify-content: flex-end; gap: 0.6rem;
          flex-shrink: 0;
        }
      `}</style>
      {isOpen && ReactDOM.createPortal(<div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />, document.body)}
      <div
        className={`modal modal-blur fade${isOpen ? ' show' : ''}`} style={{ display: isOpen ? 'block' : 'none' }}
        id="modal-view-sales-target"
        tabIndex="-1"
        role="dialog"
      >
        <div className="modal-dialog modal-md modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">View Sales Target Details</h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={onClose}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div className="row mb-3">
                <div className="col-4 fw-bold">Year</div>
                <div className="col-8">{data.year || '-'}</div>
              </div>
              <div className="row mb-3">
                <div className="col-4 fw-bold">Department</div>
                <div className="col-8">{data.departmentName || data.department_name || '-'}</div>
              </div>
              <div className="row mb-3">
                <div className="col-4 fw-bold">Department Target (IDR)</div>
                <div className="col-8">{formatCurrency(data.amount)}</div>
              </div>
              <div className="row mb-3">
                <div className="col-4 fw-bold">Allocated to Sales</div>
                <div className="col-8">
                  {formatCurrency(data.allocatedAmount || data.allocated_amount || 0)}
                </div>
              </div>
              {Array.isArray(data.details) && data.details.length > 0 && (
                <div className="row mb-3">
                  <div className="col-12">
                    <h6>Sales Targets</h6>
                    <div className="table-responsive">
                      <table className="table table-sm table-striped">
                        <thead>
                          <tr>
                            <th>Sales</th>
                            <th>Target (IDR)</th>
                            <th>Contract Won Target (IDR)</th>
                            <th>Monthly Breakdown</th>
                            <th>Contract Won Monthly Breakdown</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.details.map(detail => (
                            <React.Fragment key={detail.id || detail.userId}>
                              <tr>
                                <td>{detail.userName || `User #${detail.userId}`}</td>
                                <td>{formatCurrency(detail.amount)}</td>
                                <td>{formatCurrency(detail.contractWonAmount)}</td>
                                <td>
                                  {detail.hasMonthlyBreakdown ? (
                                    <span className="badge bg-success">Yes</span>
                                  ) : (
                                    <span className="badge bg-secondary">No</span>
                                  )}
                                </td>
                                <td>
                                  {detail.hasContractWonMonthlyBreakdown ? (
                                    <span className="badge bg-success">Yes</span>
                                  ) : (
                                    <span className="badge bg-secondary">No</span>
                                  )}
                                </td>
                              </tr>
                              {detail.hasMonthlyBreakdown && detail.monthlyBreakdown && (
                                <tr>
                                  <td colSpan="5">
                                    <div className="ps-4">
                                      <strong className="text-muted">Monthly Distribution:</strong>
                                      <div className="row g-2 mt-1">
                                        {Object.entries(detail.monthlyBreakdown).map(([month, amount]) => {
                                          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                                                            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                          return (
                                            <div className="col-md-3 col-sm-4 col-6" key={month}>
                                              <small>
                                                <strong>{monthNames[parseInt(month) - 1]}:</strong> {formatCurrency(amount)}
                                              </small>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                              {detail.hasContractWonMonthlyBreakdown && detail.contractWonMonthlyBreakdown && (
                                <tr>
                                  <td colSpan="5">
                                    <div className="ps-4">
                                      <strong className="text-muted">Contract Won Monthly Distribution:</strong>
                                      <div className="row g-2 mt-1">
                                        {Object.entries(detail.contractWonMonthlyBreakdown).map(([month, amount]) => {
                                          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                                                            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                          return (
                                            <div className="col-md-3 col-sm-4 col-6" key={month}>
                                              <small>
                                                <strong>{monthNames[parseInt(month) - 1]}:</strong> {formatCurrency(amount)}
                                              </small>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
              <div className="row mb-3">
                <div className="col-4 fw-bold">Create Date</div>
                <div className="col-8">{formatDate(data.createdAt || data.created_at)}</div>
              </div>
              <div className="row mb-3">
                <div className="col-4 fw-bold">Update Date</div>
                <div className="col-8">{formatDate(data.updatedAt || data.updated_at)}</div>
              </div>
              <div className="row mb-3">
                <div className="col-4 fw-bold">Created By</div>
                <div className="col-8">
                  {data.createdByUser
                    ? `${data.createdByUser.firstName || ''} ${data.createdByUser.lastName || ''}`.trim() ||
                      data.createdByUser.username ||
                      '-'
                    : (data.createdBy || data.created_by || '-')}
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-4 fw-bold">Updated By</div>
                <div className="col-8">
                  {data.updatedByUser
                    ? `${data.updatedByUser.firstName || ''} ${data.updatedByUser.lastName || ''}`.trim() ||
                      data.updatedByUser.username ||
                      '-'
                    : (data.updatedBy || data.updated_by || '-')}
                </div>
              </div>
            </div>
            <div className="inv-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

ViewSalesTarget.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.object
};

export default ViewSalesTarget;

